/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from '@mui/material';
import { useCallback, useContext, useState } from 'react';

import { StatusCode } from '../../../../../api/enumerations';
import { discreditLicensedCompany } from '../../../../../api/theHive/licensedCompanies';
import { DiscreditCompanyProps } from '../../../../../api/theHive/licensedCompanies/types';
import { MultilineTextField } from '../../../../../components/CustomInput';
import { ButtonBox } from '../../../../../components/UI/Box';
import { WhiteCircularProgress } from '../../../../../components/UI/Typography';
import { IconCloseMS } from '../../../../../constants/icons';
import { GlobalContext } from '../../../../../context/global';
import useErrorMessage from '../../../../../hooks/useErrorMessage';
import useGeneral from '../../../../../hooks/useGeneral';
import {
  CloseButtonBox,
  CloseIcon,
  DialogContainer,
  OutlinedButton,
  StyledDialog,
  TextTypography,
} from './styles';

interface DiscreditCompanyDialogProps {
  noticeId: number;
  licensedCompanyId: number;
  callback: () => Promise<void> | void;
}

export function DiscreditCompanyDialog({
  noticeId,
  licensedCompanyId,
  callback,
}: DiscreditCompanyDialogProps): JSX.Element {
  const [discreditReason, setDiscreditReason] = useState('');
  const [removeAccreditationLoading, setRemoveAccreditationLoading] =
    useState(false);

  const { open, handleClose, handleOpen } = useGeneral();
  const { getErrorMessage } = useErrorMessage();
  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);

  const close = (): void => {
    setDiscreditReason('');
    handleClose();
  };

  const handleRemoveAccreditation = useCallback(async () => {
    if (discreditReason === '') {
      setSnackbarMessage('Informe o motivo do descredenciamento.');
      setErrorMessage(true);
      setOpenSnackbar(true);
      return;
    }
    try {
      setRemoveAccreditationLoading(true);
      const props: DiscreditCompanyProps = {
        noticeId,
        licensedCompanyId,
        data: {
          discredited_reason: discreditReason,
        },
      };
      const response = await discreditLicensedCompany(props);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setSnackbarMessage('Descredenciamento realizado com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      callback();
      close();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setRemoveAccreditationLoading(false);
    }
  }, [discreditReason, noticeId, licensedCompanyId]);

  return (
    <>
      <OutlinedButton onClick={handleOpen}>descredenciar</OutlinedButton>
      <StyledDialog open={open} onClose={close}>
        <CloseButtonBox>
          <CloseIcon onClick={close}>{IconCloseMS}</CloseIcon>
        </CloseButtonBox>
        <DialogContainer>
          <TextTypography fontWeight={600}>
            Informe o motivo do descredenciamento da empresa
          </TextTypography>
          <MultilineTextField
            id="discredit-reason"
            label=""
            placeholder="Descreva..."
            value={discreditReason}
            setValue={setDiscreditReason}
            maxLength={500}
          />

          <ButtonBox gap={2} my={2.5}>
            <Button
              sx={{ width: '200px', height: '48px' }}
              disabled={removeAccreditationLoading}
              onClick={handleRemoveAccreditation}
            >
              {removeAccreditationLoading ? (
                <WhiteCircularProgress size={22} />
              ) : (
                'enviar'
              )}
            </Button>
            <OutlinedButton
              sx={{ maxWidth: '200px', width: '200px', height: '48px' }}
              disabled={removeAccreditationLoading}
              onClick={close}
            >
              cancelar
            </OutlinedButton>
          </ButtonBox>
        </DialogContainer>
      </StyledDialog>
    </>
  );
}
